import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Rocket, Sparkles, ArrowLeft, Phone, ImagePlus, X, Eye, EyeOff, Trash2, AlertTriangle, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useGender, readGender } from "../lib/gender";
import { stripBidiControls, isValidEmail } from "../lib/sanitize";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { GenderModal } from "../components/GenderModal";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [_gender, _setGender, t] = useGender();
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    email: "",
    password: "",
    confirm: "",
    company_logo: "",
  });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  // Permanent account deletion (existing customer who wants to wipe data)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delEmail, setDelEmail] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  // Gender modal — popped on mount if visitor hasn't set their preferred
  // Hebrew form yet.  This is the canonical place because it's the very
  // first screen of the customer journey that requires personal data.
  const [genderOpen, setGenderOpen] = useState(false);
  // Google-signup blocker dialog — captured by clicking the Google button.
  // We need first-signup users to fill the business form manually so the
  // platform can persist required fields (company name, phone, logo).
  const [googleBlockerOpen, setGoogleBlockerOpen] = useState(false);

  // CRITICAL: on mount, force-clear any leftover session so a registration
  // request always starts from a clean slate. Without this, if the user was
  // already logged in (e.g. as super_admin) the old token would attach to
  // every request and the post-register redirect would land them on the
  // OLD account's home (the super-admin grid /S.ADMIN) — exactly the bug
  // Shany reported.
  useEffect(() => {
    try {
      localStorage.removeItem("flowup_auth_token");
      sessionStorage.removeItem("flowup_auth_token");
    } catch { /* no-op */ }
    // Also drop the httpOnly cookie server-side. Fire-and-forget.
    api.post("/auth/logout").catch(() => {});
  }, []);

  // Pop the gender chooser the moment the registration screen mounts,
  // unless the visitor has already picked (e.g., via the homepage CTA).
  // Tiny delay so it animates in after the page is visible.
  useEffect(() => {
    if (!readGender()) {
      const t = setTimeout(() => setGenderOpen(true), 250);
      return () => clearTimeout(t);
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Permanent account deletion via existing credentials (NOT logged in).
  // Customer enters their existing email + password → backend verifies →
  // cascade-deletes their company + every related collection.
  const doDeleteByCreds = async (e) => {
    e.preventDefault();
    if (!isValidEmail(delEmail)) {
      toast.error("פורמט אימייל לא תקין");
      return;
    }
    if (!delPassword) {
      toast.error("הזן סיסמה");
      return;
    }
    setDelBusy(true);
    try {
      await api.post("/auth/delete-by-credentials", {
        email: stripBidiControls(delEmail).trim(),
        password: delPassword,
      });
      try {
        localStorage.removeItem("flowup_auth_token");
        sessionStorage.removeItem("flowup_auth_token");
      } catch { /* no-op */ }
      toast.success("החשבון נמחק לצמיתות. להתראות 👋");
      setDeleteOpen(false);
      setDelEmail(""); setDelPassword("");
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "מחיקת החשבון נכשלה");
    } finally {
      setDelBusy(false);
    }
  };

  // Google-based account deletion: flag the intent in storage so the
  // /auth/callback page calls the delete endpoint instead of normal login.
  const startDeleteByGoogle = () => {
    try {
      sessionStorage.setItem("flowup_delete_intent", "1");
    } catch { /* no-op */ }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("נא לבחור קובץ תמונה");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("גודל מקסימלי: 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => update("company_logo", ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(form.email)) {
      toast.error("פורמט אימייל לא תקין");
      return;
    }
    if (form.password.length < 6) {
      toast.error("הסיסמה חייבת להכיל לפחות 6 תווים");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("הסיסמאות לא תואמות");
      return;
    }
    setBusy(true);
    try {
      const res = await register({
        email: stripBidiControls(form.email).trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        company_name: form.company_name.trim() || form.full_name.trim(),
        company_logo: form.company_logo,
      });
      toast.success("ברוכים הבאים ל-FlowUp! 🚀");
      // Take the user STRAIGHT to their personal dashboard so the brief CTA
      // appears. Falls back to /login only if for some reason the backend
      // didn't return a slug (shouldn't happen).
      const slug = res?.slug || res?.company?.slug || "";
      navigate(slug ? `/${slug}` : "/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ביצירת חשבון");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-bl from-brand-50 via-white to-stone-50">
      <Toaster position="top-center" richColors dir="rtl" />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mb-6" data-testid="register-back-home">
          <ArrowLeft className="h-3.5 w-3.5" />
          חזרה לדף הבית
        </Link>

        {/* Personal-onboarding banner — gives user the option to call */}
        <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-3 mb-4 text-center">
          <p className="text-xs font-bold text-amber-900 mb-1">
            ☎️ חיבור מהיר עם מתכנת בליווי אישי
          </p>
          <a href="tel:0504944192" className="inline-flex items-center gap-1 text-sm font-bold text-amber-700 hover:text-amber-900" data-testid="register-call-cta">
            <Phone className="h-3.5 w-3.5" />
            נא לצלצל לטלפון: 050-4944192
          </a>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-8 space-y-5" dir="rtl">
          <div className="text-center space-y-2">
            <img
              src="/logo.png"
              alt="FlowUp"
              className="h-16 w-auto mx-auto object-contain mb-2"
              data-testid="register-flowup-logo"
            />
            <div className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3 w-3" />
              בנה אוטומציה חדשה
            </div>
            <h1 className="font-heading text-3xl font-bold leading-tight">{t("בוא נכיר את העסק שלך", "בואי נכיר את העסק שלך")}</h1>
            <p className="text-sm text-stone-600">מלא את הפרטים פעם אחת — ונעבור לבנות יחד את האוטומציה</p>
          </div>

          <form onSubmit={submit} className="space-y-3.5" data-testid="register-form">
            <div>
              <Label>שמך המלא</Label>
              <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="רחל כהן" className="mt-1" required data-testid="register-name-input" autoFocus />
            </div>
            <div>
              <Label>שם העסק</Label>
              <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="הסטודיו של רחל" className="mt-1" required data-testid="register-company-input" />
            </div>

            {/* === לוגו העסק (יחליף את לוגו FlowUp בדשבורד) === */}
            <div>
              <Label>לוגו העסק <span className="text-stone-400 text-xs font-normal">(יוצג בדשבורד שלך כתמונה ראשית)</span></Label>
              {form.company_logo ? (
                <div className="mt-1 relative inline-block">
                  <img
                    src={form.company_logo}
                    alt="לוגו"
                    className="h-24 w-24 rounded-xl object-contain bg-stone-50 border-2 border-brand/30 p-1"
                    data-testid="register-logo-preview"
                  />
                  <button
                    type="button"
                    onClick={() => update("company_logo", "")}
                    className="absolute -top-2 -left-2 h-7 w-7 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-md"
                    data-testid="register-logo-remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-1 w-full flex flex-col items-center justify-center gap-1.5 py-4 px-3 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-brand-50 hover:border-brand transition-colors"
                  data-testid="register-logo-upload"
                >
                  <ImagePlus className="h-5 w-5 text-stone-500" />
                  <span className="text-xs font-medium text-stone-700">העלה לוגו (PNG/JPG, עד 2MB)</span>
                  <span className="text-[10px] text-stone-500">לא חובה — אפשר להוסיף אחר כך</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
            </div>
            <div>
              <Label>אימייל</Label>
              <Input type="text" inputMode="email" dir="ltr" autoComplete="email" name="email" lang="en" spellCheck={false} value={form.email} onChange={(e) => update("email", stripBidiControls(e.target.value))} placeholder="you@business.com" className="mt-1 text-right" required data-testid="register-email-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-brand" />
                טלפון
              </Label>
              <Input type="tel" dir="ltr" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="050-1234567" className="mt-1 text-right" required data-testid="register-phone-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>סיסמה</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} dir="ltr" value={form.password} onChange={(e) => update("password", e.target.value)} className="mt-1 text-right pl-9" required minLength={6} data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 text-stone-400 hover:text-stone-600" tabIndex={-1} data-testid="register-password-toggle" aria-label={showPw ? "הסתר" : "הצג"}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>אישור סיסמה</Label>
                <div className="relative">
                  <Input type={showPw2 ? "text" : "password"} dir="ltr" value={form.confirm} onChange={(e) => update("confirm", e.target.value)} className="mt-1 text-right pl-9" required data-testid="register-confirm-input" />
                  <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 text-stone-400 hover:text-stone-600" tabIndex={-1} data-testid="register-confirm-toggle" aria-label={showPw2 ? "הסתר" : "הצג"}>
                    {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-white font-bold text-base py-6 shadow-lg shadow-brand/30" data-testid="register-submit-button">
              <Rocket className="h-5 w-5 ml-2" />
              {busy ? "יוצר חשבון..." : t("המשך לבנות את האוטומציה שלי", "המשיכי לבנות את האוטומציה שלי")}
              <ArrowLeft className="h-4 w-4 mr-2" />
            </Button>
          </form>

          {/* Google sign-up alternative — auto-creates owner + company */}
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center text-[11px]">
              <span className="bg-white px-2 text-stone-400">או</span>
            </div>
          </div>
          <GoogleSignInButton label="הרשמי עם Google" testid="register-google-btn" onClickOverride={() => setGoogleBlockerOpen(true)} />

          <div className="text-center text-sm text-stone-600 pt-2 border-t border-stone-100">
            כבר יש חשבון?{" "}
            <Link to="/login" className="text-brand font-semibold hover:underline" data-testid="register-login-link">
              היכנס
            </Link>
          </div>

          {/* Danger zone — permanent account deletion (no session required).
              Two paths: enter email+password OR sign in with Google. */}
          <div className="text-center pt-3 border-t border-dashed border-rose-200">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-700 hover:underline transition"
              data-testid="register-open-delete-account-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              מחק חשבון לצמיתות
            </button>
          </div>
        </div>
      </div>

      {/* Gender chooser — pops on mount if no preference saved yet */}
      <GenderModal
        open={genderOpen}
        onSelect={() => setGenderOpen(false)}
      />

      {/* Google sign-up blocker — first-time signups MUST fill the
          manual business form so we capture name/phone/logo. After their
          first registration they can use Google for login. */}
      <AlertDialog open={googleBlockerOpen} onOpenChange={setGoogleBlockerOpen}>
        <AlertDialogContent dir="rtl" className="sm:max-w-md" data-testid="register-google-blocker-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right flex items-center gap-2 text-brand">
              <Info className="h-5 w-5" />
              רגע לפני שנמשיך עם Google
            </AlertDialogTitle>
            <AlertDialogDescription className="text-right text-sm leading-relaxed text-stone-700">
              אנחנו יודעים שזה נוח ומהיר להתחבר עם גוגל אבל — אנחנו צריכים ממך פרטים כגון שם פרטי, שם העסק, לוגו ועוד.
              <br /><br />
              תרשם רגיל בפעם הזו, לאחר מכן בכניסה הבאה תוכל להיכנס מהגוגל בקלות 💚
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <Button
              type="button"
              onClick={() => setGoogleBlockerOpen(false)}
              className="bg-brand hover:bg-brand-hover text-white font-bold flex-1"
              data-testid="register-google-blocker-continue-btn"
            >
              <ArrowLeft className="h-4 w-4 ml-2" />
              המשך עם הכרות העסק
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent account deletion dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent dir="rtl" className="sm:max-w-md" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              מחיקת חשבון לצמיתות
            </AlertDialogTitle>
            <AlertDialogDescription className="text-right text-sm leading-relaxed">
              פעולה זו תמחק את העסק שלך, כל הנתונים, ההיסטוריה והיתרה. <strong>לא ניתן לשחזר.</strong>
              {" "}אמת את הזהות שלך עם פרטי התחברות או עם Google.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <form onSubmit={doDeleteByCreds} className="space-y-3 pt-1" data-testid="delete-account-form">
            <div>
              <Label className="text-sm">אימייל</Label>
              <Input
                type="text"
                inputMode="email"
                dir="ltr"
                autoComplete="email"
                value={delEmail}
                onChange={(e) => setDelEmail(stripBidiControls(e.target.value))}
                placeholder="you@business.com"
                className="mt-1 text-right"
                required
                data-testid="delete-email-input"
              />
            </div>
            <div>
              <Label className="text-sm">סיסמה</Label>
              <Input
                type="password"
                dir="ltr"
                value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)}
                className="mt-1 text-right"
                required
                data-testid="delete-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={delBusy}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold"
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 ml-2" />
              {delBusy ? "מוחק..." : "מחק לצמיתות עם פרטי התחברות"}
            </Button>
          </form>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center text-[11px]"><span className="bg-white px-2 text-stone-400">או</span></div>
          </div>

          <div onClick={startDeleteByGoogle} data-testid="delete-google-wrap">
            <GoogleSignInButton label="מחק לצמיתות עם חשבון Google" testid="delete-google-btn" />
          </div>

          <AlertDialogFooter>
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              className="w-full text-center text-sm text-stone-500 hover:text-stone-700 mt-2"
              disabled={delBusy}
              data-testid="delete-cancel-btn"
            >
              ביטול
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
